import { initialMoney, sum, minus, times, Money, toMoney, toNumber } from '@/modules/shared/utils/money'
import { computed } from 'vue'
import { BatchCall, Call } from '../stores/capital-call-store'
import { Commitment, Entity } from '../stores/better-entity-store'

type TransformedTransfer = {
  id: string | null
  is_active: boolean
  name: string
  capital: Money
  commitment: Money
  commitment_date: string
  commitment_remaining: Money
  pre_commitment_remaining: Money
  ownership: number | null
  fund_total_capital_committed: Money
  investor_set_commitment_id: string | null
}
type Macros = {
  total_capital: Money | number
}

const calculateCapital = (transfer: TransformedTransfer, macros: Macros, config) => {
  const ownership = transfer.ownership
  let total_capital = macros.total_capital || initialMoney
  if (config.selectedCapitalType === '%') {
    total_capital = times(transfer.commitment, ((macros.total_capital as number) || 0) / 100)
  } else {
    total_capital = times((macros.total_capital as Money) || initialMoney, ownership)
  }
  return total_capital
}
const calculateCommitmentRemaining = (transfer: TransformedTransfer): Money => {
  const commitment_remaining = minus(
    transfer.pre_commitment_remaining || initialMoney,
    transfer.capital || initialMoney,
  )
  return commitment_remaining
}

export const useCapitalCall = (configComputed) => {
  const config = computed(() => configComputed.value)

  const transformTransfer = (transfer: Call, capital_call): TransformedTransfer => {
    const t: TransformedTransfer = {
      id: transfer.id,
      is_active: true,
      name: transfer.commitment.investor.name,
      capital: transfer.capital,
      commitment: transfer.commitment.capital,
      commitment_date: transfer.commitment.date,
      commitment_remaining: initialMoney,
      pre_commitment_remaining: transfer.commitment.commitment_remaining,
      ownership: transfer.commitment.ownership,
      fund_total_capital_committed: capital_call.fund_total_capital_committed,
      investor_set_commitment_id: transfer.investor_set_commitment_id,
    }

    t.commitment_remaining = calculateCommitmentRemaining(t)

    return t
  }

  const resetTransfer = (transfer: TransformedTransfer) => {
    transfer.capital = initialMoney
    transfer.commitment_remaining = calculateCommitmentRemaining(transfer)
    return transfer
  }

  const updateTransfer = (transfer: TransformedTransfer) => {
    transfer.commitment_remaining = calculateCommitmentRemaining(transfer)
    return transfer
  }

  const calculateAggregate = (transfers: TransformedTransfer[]) => {
    const aggregates = {
      capital: initialMoney,
      commitment_remaining: initialMoney,
    }
    transfers.forEach((transfer) => {
      if (transfer.is_active) {
        aggregates.capital = sum([aggregates.capital, transfer.capital || initialMoney])
      }
      aggregates.commitment_remaining = sum([aggregates.commitment_remaining, transfer.commitment_remaining])
    })

    return aggregates
  }

  const updateMacroTransfer = (transfer: TransformedTransfer, macros: Macros) => {
    transfer.capital = calculateCapital(transfer, macros, config.value)
    transfer.commitment_remaining = calculateCommitmentRemaining(transfer)
    return transfer
  }

  const updateMultipleCommitments = (group, macros) => {
    if (config.value.selectedCapitalType === '%') {
      group.forEach((transfer) => {
        updateMacroTransfer(transfer, macros)
      })
      return
    }

    const total_capital_amount = toNumber(macros.total_capital || initialMoney)
    const ownershipSum = group.reduce((sum, transfer) => sum + parseFloat(transfer.ownership), 0)
    let remainingCapital = total_capital_amount * parseFloat(ownershipSum).toFixed(2)

    group.forEach((transfer) => {
      const commitmentRemaining = toNumber(transfer.pre_commitment_remaining || initialMoney)
      const allocation = Math.min(remainingCapital, commitmentRemaining) || 0

      transfer.capital = toMoney(allocation, transfer.commitment.currency)
      remainingCapital -= allocation
      transfer.commitment_remaining = calculateCommitmentRemaining(transfer)

      if (transfer.commitment_remaining <= 0) return
    })

    if (remainingCapital > 0) {
      group.forEach((transfer) => {
        const commitmentRemaining = remainingCapital
        const allocation = ownershipSum / 2
        const sum = toNumber(transfer.capital) + commitmentRemaining * allocation

        transfer.capital = toMoney(sum, transfer.commitment.currency)
        transfer.commitment_remaining = calculateCommitmentRemaining(transfer)
      })
    }
  }

  return {
    resetTransfer,
    updateTransfer,
    calculateAggregate,
    updateMacroTransfer,
    updateMultipleCommitments,
    transformTransfer,
  }
}

export const newCapitalCallFromCommitments = (commitments: Commitment[], entity: Entity): BatchCall => {
  const fund_total_capital_committed = commitments.length
    ? sum(commitments.map((c) => c.committed || initialMoney))
    : initialMoney
  const batchCall: BatchCall = {
    id: null,
    prior: true,
    date: new Date().toDateString(),
    schedule_date: null,
    notes: null,
    wiring_instructions: entity.computed_wiring_instructions,
    selected_capital_type: '$',
    currency: entity.currency,
    entered_capital_amount: null,
    fund_total_capital_committed: fund_total_capital_committed,
    capital_call_transfers: [],
  }

  commitments.forEach((commitment) => {
    const ownership =
      entity.ownership_type === 'assigned' ? commitment.ownership_override : commitment.ownership_by_commitment || 0
    if (!ownership) return

    const transfer: Call = {
      id: null,
      capital: initialMoney,
      investor_set_commitment_id: commitment.id,
      investor_set_transaction_id: null,
      commitment: {
        id: commitment.id,
        capital: commitment.committed || initialMoney,
        date: commitment.date,
        investor: {
          _cid: commitment.shareholder.cid,
          id: commitment.shareholder.id,
          name: commitment.shareholder.name || '',
        },
        commitment_remaining: commitment.commitment_remaining || initialMoney,
        ownership: ownership,
      },
    }
    batchCall.capital_call_transfers.push(transfer)
  })

  return batchCall
}
